import {
  AcademicCapIcon,
  ArrowRightIcon,
  BriefcaseIcon,
  HeartIcon,
  ScaleIcon,
  UsersIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import React, { useEffect, useState } from "react";

import { Link } from "react-router-dom";

const Home: React.FC = () => {
  const [isWorkshopModalOpen, setIsWorkshopModalOpen] = useState(false);

  useEffect(() => {
    const timer = globalThis.setTimeout(() => {
      setIsWorkshopModalOpen(true);
    }, 4000);

    return () => globalThis.clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (!isWorkshopModalOpen) {
      return;
    }

    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, [isWorkshopModalOpen]);

  const focusAreas = [
    {
      name: "Legal Advocacy",
      description:
        "Fighting for justice and equal representation in the legal system.",
      icon: ScaleIcon,
      color: "text-blue-600",
    },
    {
      name: "Education",
      description:
        "Promoting educational opportunities and resources for Black Canadians.",
      icon: AcademicCapIcon,
      color: "text-green-600",
    },
    {
      name: "Community Building",
      description: "Strengthening bonds and creating supportive networks.",
      icon: UsersIcon,
      color: "text-purple-600",
    },
    {
      name: "Workplace Equality",
      description:
        "Ensuring fair treatment and opportunities in professional environments.",
      icon: BriefcaseIcon,
      color: "text-orange-600",
    },
  ];

  const recentNews = [
    {
      id: 1,
      title: "PBCN Launches New Mentorship Program",
      excerpt:
        "Connecting young Black professionals with experienced mentors across various industries.",
      date: "2024-08-15",
      image:
        "https://images.unsplash.com/photo-1573165706511-3ffde6ef1fe3?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80",
    },
    {
      id: 2,
      title: "Legal Workshop: Know Your Rights",
      excerpt:
        "Free workshop covering employment law, housing rights, and civil liberties for Black Canadians.",
      date: "2024-08-10",
      image:
        "https://images.unsplash.com/photo-1573164574511-73c773193279?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80",
    },
    {
      id: 3,
      title: "Cultural Heritage Celebration",
      excerpt:
        "Annual celebration highlighting the rich cultural contributions of Black Canadians.",
      date: "2024-08-05",
      image:
        "https://plus.unsplash.com/premium_photo-1732464747450-2eaa741d181c?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80",
    },
  ];

  return (
    <div className="min-h-screen">
      {isWorkshopModalOpen && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center p-4">
          <button
            type="button"
            className="absolute inset-0 bg-black/60"
            onClick={() => setIsWorkshopModalOpen(false)}
            aria-label="Close modal"
          />

          <dialog
            open
            className="relative z-10 w-full max-w-3xl p-0 m-0 bg-transparent border-none"
            aria-label="Professional Integration Workshop"
          >
            <div className="relative overflow-hidden bg-white shadow-2xl rounded-2xl">
              <button
                type="button"
                className="absolute z-10 p-2 text-gray-700 transition-colors bg-white rounded-full top-3 right-3 hover:text-gray-900"
                onClick={() => setIsWorkshopModalOpen(false)}
                aria-label="Close modal"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>

              <div className="grid grid-cols-1 md:grid-cols-2">
                <div className="h-64 md:h-full">
                  <img
                    src="/assets/cohort.jpg"
                    alt="Professional Integration Workshop"
                    className="object-cover w-full h-full"
                  />
                </div>

                <div className="p-6 md:p-8">
                  <p className="mb-2 text-sm font-semibold tracking-wide uppercase text-secondary-700">
                    New Program
                  </p>
                  <h3 className="mb-3 text-2xl font-bold leading-tight text-gray-900 md:text-3xl font-heading">
                    Professional Integration Workshop
                  </h3>
                  <p className="mb-5 text-gray-700">
                    Build career-ready skills through resume development,
                    networking support, and practical integration guidance.
                  </p>
                  <ul className="mb-6 space-y-2 text-gray-700">
                    <li>• Skills mapping and career planning</li>
                    <li>• Resume development and mentoring</li>
                    <li>• Professional networking opportunities</li>
                  </ul>
                  <div className="flex flex-col gap-3 sm:flex-row">
                    <a
                      href="https://forms.cloud.microsoft/pages/responsepage.aspx?id=w_kZIWEnp0WbF0nuOdwjpRcBBVVRL_dJorVPMKRotb1UNTk4VDhMTlQzTzFEUktCSFJCTkRNTlZVRi4u&origin=lprLink&route=shorturl"
                      className="inline-flex items-center justify-center px-5 py-3 font-semibold text-white transition-colors rounded-lg bg-secondary-600 hover:bg-secondary-700"
                      onClick={() => setIsWorkshopModalOpen(false)}
                    >
                      Register Now
                    </a>
                    <button
                      type="button"
                      className="inline-flex items-center justify-center px-5 py-3 font-semibold text-gray-700 transition-colors border border-gray-300 rounded-lg hover:bg-gray-50"
                      onClick={() => setIsWorkshopModalOpen(false)}
                    >
                      Maybe Later
                    </button>
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-1 gap-4 p-6 bg-gray-50 min-[360px]:grid-cols-2">
                <div className="flex items-center justify-center p-3 bg-white rounded-lg">
                  <img
                    src="/assets/teachtogrow.jpg"
                    alt="Teach to Grow"
                    className="object-contain w-full h-16"
                  />
                </div>
                <div className="flex items-center justify-center p-3 bg-white rounded-lg">
                  <img
                    src="/assets/calgaryfoundation.JPG"
                    alt="Calgary Foundation"
                    className="object-contain w-full h-16"
                  />
                </div>
              </div>
            </div>
          </dialog>
        </div>
      )}

      {/* Hero Section */}
      <section className="relative pt-20 text-white bg-gradient-to-br from-primary-900 via-primary-800 to-primary-700">
        <div className="absolute inset-0 bg-black opacity-50"></div>
        <div className="relative px-4 py-24 mx-auto max-w-7xl sm:px-6 lg:px-8">
          <div className="grid items-center grid-cols-1 gap-12 lg:grid-cols-2">
            <div>
              <h1 className="mb-6 text-4xl font-bold leading-tight md:text-6xl font-heading">
                Empowering Black Canadians Through{" "}
                <span className="text-secondary-400">Unity & Action</span>
              </h1>
              <p className="mb-8 text-xl leading-relaxed text-gray-200">
                The Progressive Black Canadian Network advocates for advocacy,
                education, and opportunity for Black Canadians across all
                sectors of society. Join us in creating lasting change.
              </p>
              <div className="flex flex-col gap-4 sm:flex-row">
                <Link
                  to="/about"
                  className="inline-flex items-center px-8 py-4 font-semibold text-white transition-colors duration-200 rounded-lg bg-secondary-600 hover:bg-secondary-700"
                >
                  Learn More
                  <ArrowRightIcon className="w-5 h-5 ml-2" />
                </Link>
                <Link
                  to="/donate"
                  className="inline-flex items-center px-8 py-4 font-semibold text-white transition-colors duration-200 border-2 border-white rounded-lg hover:bg-white hover:text-primary-800"
                >
                  <HeartIcon className="w-5 h-5 mr-2" />
                  Support Our Cause
                </Link>
              </div>
            </div>
            <div className="relative">
              <img
                src="/assets/cultural-arts-1.jpg"
                alt="Community gathering and unity"
                className="rounded-lg shadow-2xl"
              />

              <div className="absolute inset-0 rounded-lg bg-gradient-to-t from-primary-900/20 to-transparent"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission Statement */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl px-4 mx-auto text-center sm:px-6 lg:px-8">
          <h2 className="mb-6 text-3xl font-bold text-gray-900 md:text-4xl font-heading">
            Our Mission
          </h2>
          <p className="text-xl leading-relaxed text-gray-700">
            To empower and connect Black communities through economic
            advancement, education, advocacy, and cultural celebration. We work
            to foster professional and business development, promote civic
            engagement, and support youth and emerging leaders across Canada.
          </p>
        </div>
      </section>

      {/* Focus Areas */}
      <section className="py-16">
        <div className="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
          <div className="mb-12 text-center">
            <h2 className="mb-4 text-3xl font-bold text-gray-900 md:text-4xl font-heading">
              Our Focus Areas
            </h2>
            <p className="text-xl text-gray-600">
              Comprehensive approach to creating positive change
            </p>
          </div>
          <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-4">
            {focusAreas.map((area) => (
              <div
                key={area.name}
                className="p-6 transition-shadow duration-300 bg-white border border-gray-100 shadow-lg rounded-xl hover:shadow-xl"
              >
                <div className="flex items-center justify-center w-12 h-12 mb-4 rounded-lg bg-gray-50">
                  <area.icon className={`h-6 w-6 ${area.color}`} />
                </div>
                <h3 className="mb-3 text-xl font-semibold text-gray-900">
                  {area.name}
                </h3>
                <p className="leading-relaxed text-gray-600">
                  {area.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Recent News */}
      <section className="py-16 bg-gray-50">
        <div className="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
          <div className="mb-12 text-center">
            <h2 className="mb-4 text-3xl font-bold text-gray-900 md:text-4xl font-heading">
              Recent News & Events
            </h2>
            <p className="text-xl text-gray-600">
              Stay updated with our latest initiatives and community activities
            </p>
          </div>
          <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
            {recentNews.map((item) => (
              <article
                key={item.id}
                className="overflow-hidden transition-shadow duration-300 bg-white shadow-lg rounded-xl hover:shadow-xl"
              >
                <div className="aspect-w-16 aspect-h-9">
                  <img
                    src={item.image}
                    alt={item.title}
                    className="object-cover w-full h-48"
                  />
                </div>
                <div className="p-6">
                  <div className="mb-2 text-sm text-gray-500">
                    {new Date(item.date).toLocaleDateString("en-US", {
                      year: "numeric",
                      month: "long",
                      day: "numeric",
                    })}
                  </div>
                  <h3 className="mb-3 text-xl font-semibold text-gray-900">
                    {item.title}
                  </h3>
                  <p className="leading-relaxed text-gray-600">
                    {item.excerpt}
                  </p>
                </div>
              </article>
            ))}
          </div>
          <div className="mt-12 text-center">
            <Link
              to="/gallery"
              className="inline-flex items-center px-6 py-3 font-semibold text-white transition-colors duration-200 rounded-lg bg-primary-600 hover:bg-primary-700"
            >
              View All Events
              <ArrowRightIcon className="w-5 h-5 ml-2" />
            </Link>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-16 text-white bg-primary-800">
        <div className="max-w-4xl px-4 mx-auto text-center sm:px-6 lg:px-8">
          <h2 className="mb-6 text-3xl font-bold md:text-4xl font-heading">
            Join Our Movement
          </h2>
          <p className="mb-8 text-xl leading-relaxed text-gray-200">
            Together, we can create lasting change and build a more equitable
            future for all Black Canadians. Your voice and support matter.
          </p>
          <div className="flex flex-col justify-center gap-4 sm:flex-row">
            <Link
              to="/contact"
              className="inline-flex items-center px-8 py-4 font-semibold text-white transition-colors duration-200 rounded-lg bg-secondary-600 hover:bg-secondary-700"
            >
              Get Involved
            </Link>
            <Link
              to="/donate"
              className="inline-flex items-center px-8 py-4 font-semibold text-white transition-colors duration-200 border-2 border-white rounded-lg hover:bg-white hover:text-primary-800"
            >
              <HeartIcon className="w-5 h-5 mr-2" />
              Donate Now
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
